import React, { useEffect, useMemo, useState } from 'react';

const HOURS = Array.from({ length: 12 }, (_, index) => index + 1);
const MINUTES = Array.from({ length: 12 }, (_, index) => index * 5);

function parseTimeValue(value) {
    const [rawHour, rawMinute] = String(value || '08:30').split(':');
    const hour24 = Math.max(0, Math.min(23, Number(rawHour) || 0));
    const minute = Math.max(0, Math.min(59, Number(rawMinute) || 0));
    const period = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 || 12;
    return { hour12, minute, period };
}

function formatTimeValue({ hour12, minute, period }) {
    const normalizedHour = Number(hour12) % 12;
    const hour24 = period === 'PM' ? normalizedHour + 12 : normalizedHour;
    return `${String(hour24).padStart(2, '0')}:${String(Number(minute) || 0).padStart(2, '0')}`;
}

function formatDisplayTime(value) {
    const { hour12, minute, period } = parseTimeValue(value);
    return `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
}

function ClockOption({ value, selected, mode, onSelect }) {
    const index = mode === 'hour' ? value % 12 : value / 5;
    const angle = (index / 12) * 360 - 90;
    const radius = 42;
    const x = 50 + radius * Math.cos((angle * Math.PI) / 180);
    const y = 50 + radius * Math.sin((angle * Math.PI) / 180);

    return (
        <button
            type="button"
            onClick={() => onSelect(value)}
            className={`absolute flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-xs font-black transition ${
                selected
                    ? 'bg-[#D81F26] text-white shadow-[0_4px_12px_rgba(216,31,38,0.28)]'
                    : 'text-[#373737] hover:bg-[#eaeaea]'
            }`}
            style={{ left: `${x}%`, top: `${y}%` }}
            aria-pressed={selected}
        >
            {mode === 'minute' ? String(value).padStart(2, '0') : value}
        </button>
    );
}

export default function AppointmentClockPicker({ value, onChange, id = 'appointment-clock-picker', openUp = false, align = 'right', placeholder = '' }) {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('hour');
    const [draft, setDraft] = useState(() => parseTimeValue(value));

    useEffect(() => {
        if (!open) setDraft(parseTimeValue(value));
    }, [open, value]);

    const handAngle = useMemo(() => {
        const index = mode === 'hour' ? draft.hour12 % 12 : draft.minute / 5;
        return (index / 12) * 360;
    }, [draft.hour12, draft.minute, mode]);

    const options = mode === 'hour' ? HOURS : MINUTES;

    function commit() {
        onChange?.(formatTimeValue(draft));
        setOpen(false);
        setMode('hour');
    }

    function cancel() {
        setDraft(parseTimeValue(value));
        setOpen(false);
        setMode('hour');
    }

    return (
        <div className="relative">
            <button
                id={id}
                type="button"
                onClick={() => {
                    setDraft(parseTimeValue(value));
                    setMode('hour');
                    setOpen((current) => !current);
                }}
                className="flex h-12 w-full items-center justify-between rounded-xl border border-[#d9d9d9] bg-white px-3 text-left text-sm font-black text-[#414141] outline-none transition hover:border-[#B3B3B3] focus:border-[#D81F26] focus:ring-2 focus:ring-[#D81F26]/15"
                aria-haspopup="dialog"
                aria-expanded={open}
            >
                <span className={value ? '' : 'text-[#9e9e9e]'}>{value ? formatDisplayTime(value) : (placeholder || formatDisplayTime(value))}</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" />
                </svg>
            </button>

            {open && (
                <div
                    className={`absolute z-[80] w-[min(82vw,272px)] rounded-2xl border border-[#d9d9d9] bg-white p-3 text-[#414141] shadow-[0_18px_48px_rgba(34,34,34,0.22)] ${align === 'left' ? 'left-0' : 'right-0'} ${openUp ? 'bottom-full mb-2' : 'mt-2'}`}
                    role="dialog"
                    aria-label="Select appointment time"
                >
                    <p className="text-sm font-black text-[#414141]">Select time</p>

                    <div className="mt-3 flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setMode('hour')}
                            className={`h-11 min-w-[68px] rounded-xl px-2 text-3xl font-black ${
                                mode === 'hour' ? 'bg-[#414141] text-white' : 'bg-[#F2F2F2] text-[#414141]'
                            }`}
                        >
                            {String(draft.hour12).padStart(2, '0')}
                        </button>
                        <span className="text-3xl font-black text-[#414141]">:</span>
                        <button
                            type="button"
                            onClick={() => setMode('minute')}
                            className={`h-11 min-w-[68px] rounded-xl px-2 text-3xl font-black ${
                                mode === 'minute' ? 'bg-[#414141] text-white' : 'bg-[#F2F2F2] text-[#414141]'
                            }`}
                        >
                            {String(draft.minute).padStart(2, '0')}
                        </button>
                        <div className="ml-auto flex shrink-0 gap-1">
                            {['AM', 'PM'].map((period) => (
                                <button
                                    key={period}
                                    type="button"
                                    onClick={() => setDraft((current) => ({ ...current, period }))}
                                    className={`min-h-[30px] min-w-[38px] rounded-full border px-2 text-xs font-black ${
                                        draft.period === period
                                            ? 'border-[#414141] bg-[#414141] text-white'
                                            : 'border-[#d9d9d9] bg-white text-[#414141]'
                                    }`}
                                >
                                    {period}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-4 flex items-center justify-center">
                        <div className="relative h-40 w-40 shrink-0 rounded-full bg-[#F9F9F9]">
                            <div
                                className="absolute left-1/2 top-1/2 h-[2px] w-[52px] origin-left bg-[#c3c3c3]"
                                style={{ transform: `rotate(${handAngle - 90}deg)` }}
                            />
                            <div className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#414141]" />
                            {options.map((option) => (
                                <ClockOption
                                    key={option}
                                    value={option}
                                    mode={mode}
                                    selected={mode === 'hour' ? draft.hour12 === option : draft.minute === option}
                                    onSelect={(nextValue) => {
                                        if (mode === 'hour') {
                                            setDraft((current) => ({ ...current, hour12: nextValue }));
                                            setMode('minute');
                                        } else {
                                            setDraft((current) => ({ ...current, minute: nextValue }));
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="mt-3 flex items-center justify-end gap-2">
                        <button type="button" onClick={cancel} className="min-h-[34px] rounded-xl px-3 text-sm font-black text-[#414141] hover:bg-[#F9F9F9]">
                            Cancel
                        </button>
                        <button type="button" onClick={commit} className="min-h-[34px] rounded-xl bg-[#D81F26] px-4 text-sm font-black text-white hover:bg-[#B8181E]">
                            OK
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
